import { Component } from '@theme/component';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Bepaalt wanneer een clay-animatie mag spelen.
 *
 * Het `autoplay`-attribuut staat in de markup, zodat de video ook zonder
 * JavaScript loopt. Zodra dit component er is, neemt het de regie over: het
 * pauzeert bij `prefers-reduced-motion` (dan blijft de poster staan, en dat is
 * de statische fallback-afbeelding) en het speelt alleen zolang de video in of
 * net buiten het beeld staat. Op de homepage staan zeven clay-slots; zo lopen
 * er nooit meer video's dan de bezoeker daadwerkelijk ziet.
 *
 * @typedef {object} Refs
 * @property {HTMLVideoElement} video - De video.
 *
 * @extends Component<Refs>
 */
export class ClayMediaComponent extends Component {
  requiredRefs = ['video'];

  /** @type {MediaQueryList | undefined} */
  #reducedMotion;

  /** @type {IntersectionObserver | undefined} */
  #observer;

  #inView = false;

  connectedCallback() {
    super.connectedCallback();

    const { video } = this.refs;

    // Vanaf hier bepalen wij wanneer er gespeeld wordt, niet het attribuut.
    video.autoplay = false;
    video.pause();

    this.#reducedMotion = matchMedia(REDUCED_MOTION_QUERY);
    this.#reducedMotion.addEventListener('change', this.#sync);

    this.#observer = new IntersectionObserver(this.#onIntersect, { rootMargin: '200px' });
    this.#observer.observe(video);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#observer?.disconnect();
    this.#reducedMotion?.removeEventListener('change', this.#sync);
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onIntersect = (entries) => {
    const entry = entries[entries.length - 1];
    if (!entry) return;

    this.#inView = entry.isIntersecting;
    this.#sync();
  };

  #sync = () => {
    const { video } = this.refs;
    const shouldPlay = this.#inView && this.#reducedMotion?.matches !== true;

    if (shouldPlay) {
      // play() geeft een afgewezen promise als de browser autoplay blokkeert;
      // dan blijft de poster staan, wat precies de bedoelde terugval is.
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  };
}

if (!customElements.get('clay-media-component')) {
  customElements.define('clay-media-component', ClayMediaComponent);
}
